<script>
    // Licensed under Gnu Public Licence version 2
    // Copyright (c) 2020-23 (original work) Open Assessment Technologies SA ;

    import { createEventDispatcher } from 'svelte';
    import { __, getActualKey, isRTLElement, getPointerEventCoords } from '@oat-sa-private/ui-core';
    import sliderValueFactory from './util/sliderValue.js';

    /**
     * Slider component, corresponds to <input type="range"> - a horizontal/vertical line with drag handle and numeric markings of min/max/current values
     * @property {Number} [min]
     * @property {Number} [max]
     * @property {Number} [step]
     * @property {Number} [value] - null/undefined means no value
     * @property {Boolean} [reverse] - switch rendering of min and max
     * @property {Boolean} [vertical]
     * @property {Boolean} [disabled]
     * @property {String} [ariaLabel] - aria-label
     * @property {String} [ariaDescribedBy] - aria-describedby
     * @fires 'change'
     */
    export let min = 0;
    export let max = 100;
    export let step = 1;
    export let value = null;
    export let reverse = false;
    export let vertical = false;
    export let disabled = false;
    export let ariaLabel;
    export let ariaLabelledBy;
    export let ariaDescribedBy;

    const dispatch = createEventDispatcher();
    let sliderElement;
    let dragging = false;
    let leftOffsetPercent; //style to render handle

    $: isNormalRenderingFlow = (!reverse && !vertical) || (reverse && vertical);
    $: floatValuePrecision = Math.max(
        //to round float values
        step.toString().indexOf('.') >= 0 ? step.toString().split('.')[1].length : 0,
        min.toString().indexOf('.') >= 0 ? min.toString().split('.')[1].length : 0
    );
    $: valueHelper = sliderValueFactory(min, max, step, floatValuePrecision);

    $: isValueSet = value || value === 0;
    $: {
        leftOffsetPercent = isValueSet ? ((value - min) / (max - min)) * 100 : 50; //exact middle of track by default
        if (!isNormalRenderingFlow) {
            leftOffsetPercent = 100 - leftOffsetPercent;
        }
    }

    /**
     * Handle mousedown on container
     * @param {MouseEvent} e
     */
    function handleMouseDown(e) {
        //if left mouse button
        if (e.buttons === 1) {
            dragging = true;
            handlePointerEvent(e);
        }
    }

    /**
     * Handle touchstart on container
     * @param {TouchEvent} e
     */
    function handleTouchStart(e) {
        dragging = true;
        handlePointerEvent(e);
    }

    /**
     * Handle mousemove/touchmove on window, after mousedown/touchstart on container
     * @param {MouseEvent|TouchEvent} e
     */
    function handlePointerMove(e) {
        if (dragging) {
            handlePointerEvent(e);
        }
    }

    /**
     * Handle mouseup/touchend on window, after mousedown/touchstart on container
     * @param {MouseEvent|TouchEvent} e
     */
    function handlePointerUp(e) {
        if (dragging) {
            dragging = false;
            handlePointerEvent(e);
        }
    }

    /**
     * Common handler for mousedown/touchstart, mousemove/touchmove, mouseup/touchend events
     * @param {MouseEvent|TouchEvent} e
     */
    function handlePointerEvent(e) {
        //prevent text selection Safari
        e.preventDefault();
        //check if mouuse button released or touch changed. mouseup and touchend do not fire outside the window
        if (!(e.buttons === 1 || (e.changedTouches && e.changedTouches.length === 1))) {
            dragging = false;
            return;
        }
        const domEventType = e.type;
        const target = e.target;
        const { x, y } = getPointerEventCoords(e);
        const rect = sliderElement.getBoundingClientRect();
        const isRTL = sliderElement && isRTLElement(sliderElement);
        let portion;
        if (vertical) {
            portion = (y - rect.y) / rect.height;
        } else {
            portion = (x - rect.x) / rect.width;
        }

        let newValue;
        if ((!vertical && !reverse && !isRTL) || (!vertical && reverse && isRTL) || (vertical && reverse)) {
            newValue = valueHelper.adjust(min + portion * (max - min));
        } else {
            newValue = valueHelper.adjust(max - portion * (max - min));
        }

        let position;
        if (e instanceof MouseEvent) {
            position = {
                clientX: e.clientX,
                clientY: e.clientY,
                screenX: e.screenX,
                screenY: e.screenY
            };
        }
        moveSlider(newValue, { domEventType, target, ...(position && { position }) });
    }

    /**
     * Handle keydown on container
     * @param {KeyboardEvent} e
     */
    function handleKeyDown(e) {
        const pressedKey = e.key;
        const actualKey = getActualKey(e);
        const domEventType = e.type;
        const target = e.target;

        //determine if need to handle this key
        const isKeyForXAxis = actualKey === 'right' || actualKey === 'left';
        const isKeyForYAxis =
            actualKey === 'up' || actualKey === 'down' || actualKey === 'pagedown' || actualKey === 'pageup';
        const isKeyForMinMax = actualKey === 'home' || actualKey === 'end';

        if (isKeyForXAxis || isKeyForYAxis || isKeyForMinMax) {
            //determine direction
            let isNormalDirection;
            if (isKeyForXAxis) {
                const isRTL = sliderElement && isRTLElement(sliderElement);
                isNormalDirection =
                    (!vertical && !reverse && !isRTL) || (!vertical && reverse && isRTL) || (vertical && !isRTL);
            } else if (isKeyForYAxis) {
                isNormalDirection = !reverse;
            } else if (isKeyForMinMax) {
                isNormalDirection = (vertical && reverse) || (!vertical && !reverse);
            }

            //determine quantity
            let newValue;
            const pageKeyStep = Math.max(step, step * Math.round((max - min) / 10 / step));
            if (actualKey === 'right' || actualKey === 'up') {
                newValue = valueHelper.increment(value, step * (isNormalDirection ? 1 : -1));
            } else if (actualKey === 'left' || actualKey === 'down') {
                newValue = valueHelper.increment(value, step * (isNormalDirection ? -1 : 1));
            } else if (actualKey === 'pageup') {
                newValue = valueHelper.increment(value, pageKeyStep * (isNormalDirection ? 1 : -1));
            } else if (actualKey === 'pagedown') {
                newValue = valueHelper.increment(value, pageKeyStep * (isNormalDirection ? -1 : 1));
            } else if (actualKey === 'home') {
                newValue = isNormalDirection ? min : max;
            } else if (actualKey === 'end') {
                newValue = isNormalDirection ? max : min;
            }

            //move
            moveSlider(newValue, { domEventType, pressedKey, target });
            e.preventDefault();
        }
    }

    /**
     * @typedef {{ [pressedKey]: string, domEventType: string, target: string }} EventDetail
     *
     * Change the value of the slider,
     * and fire 'change' only if value has actually changed
     * @param {Number} newValue
     * @param {Object} [detail] - additional event details to share
     * @fires 'change'
     */
    function moveSlider(newValue, detail = {}) {
        if (newValue !== value) {
            value = newValue; //like native <input type="range" />, control own value internally
            dispatch('change', { value: newValue, ...detail }); //corresponds to 'input' event of native <input type="range" />
        }
    }
</script>

<style>
    @add-mixin dir-style .slider, --dir-rtl-minus-one, -1, 1;

    .slider {
        --handle-offset: 50%;
        --slider-size: 6rem;
        --slider-half-size: 3rem;
        --handle-size: 2rem;
        --handle-half-size: 1rem;
        --minmax-offset: 1.25rem;

        width: 100%;
        font-size: var(--fontsize-body-s);
        line-height: var(--fontsize-body-s);
        position: relative;
        user-select: none; /* prevent native drag-and-drop */
    }

    .slider-body {
        width: 100%;
        height: var(--slider-size);
        position: relative;
        cursor: pointer;
    }

    .disabled .slider-body {
        cursor: not-allowed;
    }

    .track {
        width: 100%;
        padding-top: calc(var(--slider-half-size) - var(--border-medium) / 2);
        border-bottom: var(--border-medium) solid var(--color-border-default);
    }

    .minmax-labels {
        position: absolute;
        left: 0;
        right: 0;
        top: calc(var(--slider-half-size) + var(--minmax-offset));
        display: flex;
        color: var(--color-border-info);

        & > div:first-child {
            flex: 1 0 auto;
        }
        & > div:last-child {
            flex: 0 0 auto;
        }
    }

    .handle-track {
        position: absolute;
        height: 0;
        top: 0;
        left: calc(var(--handle-half-size) - 1px); /* on iOS track sometimes goes a bit below handle */
        right: calc(var(--handle-half-size) - 1px);

        & .handle {
            @add-mixin outline-focus
                calc(var(--handle-half-size) - var(--border-medium) - var(--slider-half-size) - 0.5rem);
            position: absolute;
            inset-inline-start: calc(var(--handle-offset) - var(--handle-half-size));
            top: calc(var(--slider-half-size) - var(--handle-half-size));
            width: var(--handle-size);
            height: var(--handle-size);
            background-color: var(--color-bg-default);
            border: var(--border-medium) solid var(--color-border-default);
            border-radius: 50%;

            &:before {
                /*hitbox*/
                content: '';
                position: absolute;
                left: calc(var(--handle-half-size) - var(--border-medium) - var(--slider-half-size));
                top: calc(var(--handle-half-size) - var(--border-medium) - var(--slider-half-size));
                width: var(--slider-size);
                height: var(--slider-size);
            }

            &:hover {
                background-color: var(--color-bg-actionable-secondary-hover);
            }
        }
    }

    .value-label {
        position: absolute;
        inset-inline-start: calc(var(--handle-offset));
        bottom: calc(var(--slider-half-size) + var(--handle-half-size) + 0.25rem);
        transform: translateX(calc(var(--dir-rtl-minus-one) * var(--handle-offset)));
        color: var(--color-text-link);
        font-weight: bold;
        word-break: normal;
    }

    /* vertical styles */
    .vertical {
        height: 100%;
        width: auto;
        display: flex;
        flex-direction: row;
        flex: 0 0 auto;

        & .slider-body {
            width: var(--slider-size);
            height: 100%;
        }

        & .track {
            height: 100%;
            width: calc(var(--slider-half-size) + var(--border-medium) / 2);
            padding-top: 0;
            border-bottom: 0;
            border-inline-end: var(--border-medium) solid var(--color-border-default);
        }

        & .minmax-labels {
            margin-top: 0;
            position: static;
            flex-direction: column;
            margin-inline-end: calc(var(--minmax-offset) - var(--slider-half-size));

            & > div {
                text-align: end;
                white-space: nowrap;
            }
        }

        & .handle-track {
            width: 0;
            inset-inline-start: 0;
            inset-inline-end: auto;
            height: auto;
            top: calc(var(--handle-half-size) - 1px);
            bottom: calc(var(--handle-half-size) - 1px);

            & .handle {
                inset-inline-start: calc(var(--slider-half-size) - var(--handle-half-size));
                top: calc(var(--handle-offset) - var(--handle-half-size));
            }
        }

        & .value-label {
            position: absolute;
            inset-inline-start: calc(var(--slider-half-size) + var(--minmax-offset));
            top: calc(var(--handle-offset));
            transform: translateY(calc(-1 * var(--handle-offset)));
        }
    }
</style>

<svelte:window
    on:mousemove={dragging ? handlePointerMove : void 0}
    on:touchmove={dragging ? handlePointerMove : void 0}
    on:mouseup={dragging ? handlePointerUp : void 0}
    on:touchend={dragging ? handlePointerUp : void 0} />

<div class="slider" class:vertical class:disabled>
    <div class="minmax-labels" aria-hidden={true}>
        <div>{isNormalRenderingFlow ? min : max}</div>
        <div>{isNormalRenderingFlow ? max : min}</div>
    </div>
    <!-- svelte-ignore a11y-no-static-element-interactions -->
    <div
        class="slider-body"
        style="--handle-offset: {leftOffsetPercent}%"
        bind:this={sliderElement}
        on:mousedown={!dragging && !disabled ? handleMouseDown : void 0}
        on:touchstart={!dragging && !disabled ? handleTouchStart : void 0}
        on:keydown={!disabled ? handleKeyDown : void 0}>
        <div class="track" />
        <div class="value-label" aria-hidden={true}>{isValueSet ? value.toFixed(floatValuePrecision) : ''}</div>
        <div class="handle-track">
            <!-- 'role="slider"' can't NOT have a value! no 'aria-valuenow' means middle value -->
            <div
                class="handle"
                tabindex="0"
                role="slider"
                aria-valuemax={max}
                aria-valuemin={min}
                aria-valuenow={isValueSet ? value : min}
                aria-valuetext={isValueSet ? value : __('default')}
                aria-orientation={vertical ? 'vertical' : 'horizontal'}
                aria-disabled={disabled ? true : void 0}
                aria-label={ariaLabel ? ariaLabel : void 0}
                aria-labelledby={ariaLabelledBy ? ariaLabelledBy : void 0}
                aria-describedBy={ariaDescribedBy ? ariaDescribedBy : void 0} />
        </div>
    </div>
</div>
